import React, { useEffect, useState } from 'react';
import classes from 'src/components/CustomisationPage/pages/Pages.scss';
import { RadioGroup } from 'src/core/forms';
import { Fields, ProductLocation } from 'src/components/CustomisationPage/feature/customisation.types';
import { Radio } from 'src/core/forms';
import { distinctUntilKeyChanged, tap } from 'rxjs';
import { ARTWORK_EXISTING } from 'src/components/Artwork/artwork.types';
import { NEW_ARTWORK_ID, EMPTY_ARTWORK_ID, HAVE_MY_LOGO } from 'src/constants/artwork';
import classnames from 'classnames';
import { useArtwork } from 'src/hooks/useArtwork';
import { BackgroundStyles } from 'src/components/BundleProduct/MultipleStepCustomisation/customisations.types';
import { SubmitButton } from 'src/components/Artwork/SubmitButton';

import { ArtworkType } from './ArtworkSelect';
import { AddNewLogo } from './AddNewLogo';
import { ArtworkGridWrapper } from './ArtworkSelect';

type Props = {
    locations?: ProductLocation[];
    existingArtworkId?: number;
    selectedApplicationMethod?: number;
    onNewCustomisationSubmit?: () => void;
    backgroundStyles?: BackgroundStyles;
    initialValue?: string;
};

export const RegularArtworkSelect: React.FC<Props> = ({
    locations,
    existingArtworkId,
    selectedApplicationMethod,
    onNewCustomisationSubmit,
    backgroundStyles,
    initialValue,
}) => {
    const [newLogo, setNewLogo] = useState(false);
    const { deduplicatedCustomisations, artworkGrid, applicationMethod, processed, getValueStream, api, price } =
        useArtwork();
    const computedApplicationMethod = selectedApplicationMethod ? selectedApplicationMethod : applicationMethod;
    const matchingCustomisations = deduplicatedCustomisations.filter(
        (object) => object.type === Number(computedApplicationMethod),
    );
    useEffect(() => {
        const sub = getValueStream()
            .pipe(
                distinctUntilKeyChanged('artwork'),
                tap((values) => {
                    if (values.artwork === EMPTY_ARTWORK_ID) {
                        setNewLogo(false);
                    } else if (values.artwork && values.artwork !== NEW_ARTWORK_ID && values.artwork !== 'none') {
                        const match = deduplicatedCustomisations.find(
                            (c) => String(c.artwork_id) === values.artwork || String(c.id) === values.artwork,
                        );
                        const existing_match = deduplicatedCustomisations.find(
                            (c) => c.artwork_id === existingArtworkId || c.id === existingArtworkId,
                        );
                        if (match && existing_match) {
                            match.id = existing_match.id;
                            api.setValue(ARTWORK_EXISTING, JSON.stringify(match));
                        } else if (match) {
                            api.setValue(ARTWORK_EXISTING, JSON.stringify(match));
                        }
                    } else {
                        api.setValue(ARTWORK_EXISTING, '');
                    }
                }),
            )
            .subscribe();
        return () => sub.unsubscribe();
    }, [api, getValueStream, deduplicatedCustomisations, existingArtworkId]);

    if (artworkGrid.status !== 'Success') {
        return null;
    }

    if (matchingCustomisations.length === 0) {
        return (
            <fieldset className={classes.wrapperNoCustomisations}>
                <RadioGroup field={Fields.artwork} initialValue={NEW_ARTWORK_ID}>
                    <div className={classes.radioGridAddNew}>
                        <div className={classes.addNewContainer}>
                            <SubmitButton onNewCustomisationSubmit={onNewCustomisationSubmit} price={price} />
                        </div>
                    </div>
                </RadioGroup>
            </fieldset>
        );
    }

    return (
        <fieldset className={classes.wrapper} style={backgroundStyles}>
            <RadioGroup field={Fields.artwork} initialValue={initialValue || EMPTY_ARTWORK_ID}>
                <div className={classes.wrapperInfo}>
                    {deduplicatedCustomisations?.length > 0 && (
                        <p className={classes.subHeader}>
                            <strong>Choose</strong> from your existing artwork
                        </p>
                    )}
                    <div className={classes.artworkCheckboxDesktop}>
                        <AddNewLogo id={NEW_ARTWORK_ID} description="+ Add a new logo" setNewLogo={setNewLogo} />
                    </div>
                </div>
                <div className={classes.radioGridAddNew}>
                    <ArtworkGridWrapper
                        description="+ Add a new logo"
                        locations={locations}
                        artworkGrid={artworkGrid}
                        existingArtworkId={existingArtworkId}
                        applicationMethod={applicationMethod}
                        processed={processed}
                        type={ArtworkType.Regular}
                    />
                    <div className={classes.haveMyLogo}>
                        <Radio className={classes.input} value={HAVE_MY_LOGO} id={HAVE_MY_LOGO} />
                        <label className={classnames(classes.label, classes.addNew_label)} htmlFor={HAVE_MY_LOGO}>
                            <div className={classes.addNew}>
                                <p className={classes.addNew_desc}>
                                    You already have my logo, it’s just not in my account (no setup fee will be charged)
                                </p>
                            </div>
                        </label>
                    </div>

                    <div
                        className={classnames(classes.addNewContainer, {
                            [classes.addNewContainerVisible]: newLogo,
                        })}
                    >
                        <div className={classes.closeAddNew}>
                            <Radio id={EMPTY_ARTWORK_ID} value={EMPTY_ARTWORK_ID} />
                        </div>
                        <SubmitButton onNewCustomisationSubmit={onNewCustomisationSubmit} price={price} />
                    </div>
                </div>
                <div className={classes.artworkCheckboxMobile}>
                    <AddNewLogo id={NEW_ARTWORK_ID} description="+ Add a new logo" setNewLogo={setNewLogo} />
                </div>
            </RadioGroup>
        </fieldset>
    );
};
